import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SblHttpGetOptions, SblHttpBaseOptions, SblHttpHeaders, SblHttpParams } from '@core/models/http.models';
import { SblHttpUtils } from '@core/utils/http.utils';
import { WlcmQueryFilters, WlcmQueryParams } from '@wlcm/common';
import { Observable } from 'rxjs';

type HttpOptions<R> = {
  responseType?: R;
  headers?: HttpHeaders;
  params?: HttpParams;
};

@Injectable({ providedIn: 'root' })
export class HttpApi {
  constructor(private readonly http: HttpClient) {}

  get<T>(url: string, options?: SblHttpGetOptions): Observable<T> {
    return this.http.get<T>(url, this.handleOptions(options));
  }

  post<T>(url: string, body: unknown | null, options?: SblHttpGetOptions): Observable<T> {
    return this.http.post<T>(url, body, this.handleOptions(options));
  }

  patch<T>(url: string, body: unknown | null, options?: SblHttpGetOptions): Observable<T> {
    return this.http.patch<T>(url, body, this.handleOptions(options));
  }

  delete<T>(url: string, options?: SblHttpGetOptions): Observable<T> {
    return this.http.delete<T>(url, this.handleOptions(options));
  }

  private handleOptions<R>(options: SblHttpBaseOptions | undefined): HttpOptions<R> {
    if (!options) return {};

    const params: HttpParams = this.handleParams((options.params as SblHttpParams<WlcmQueryParams>) || {});

    let headers: HttpHeaders = this.transformHeaders(options.headers as SblHttpHeaders);

    if (options.skipSuccessMessage) {
      headers = SblHttpUtils.skipSuccessMessage(headers);
    }

    return { ...options, params, headers } as HttpOptions<R>;
  }

  private handleParams(params: SblHttpParams<WlcmQueryParams>): HttpParams {
    let httpParams: HttpParams = new HttpParams();

    Object.keys(params).forEach((key: string) => {
      if (key === 'filters') {
        httpParams = SblHttpUtils.filtersToHttpParams(httpParams, params.filters as WlcmQueryFilters);
      } else {
        httpParams = httpParams.append(key, params[key as keyof WlcmQueryParams] as string | number | boolean);
      }
    });

    return httpParams;
  }

  private transformHeaders(headers: SblHttpHeaders): HttpHeaders {
    let _headers: HttpHeaders = new HttpHeaders();

    if (headers instanceof Object) {
      for (const key in headers) {
        _headers = _headers.set(key, headers[key]);
      }
    }

    return _headers;
  }
}
